import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: 'サービス案内｜岡山市の印刷会社 おかやま印刷株式会社',
    description: '紙印刷、シルク印刷、レーザー印刷など多彩な印刷技術でお客様のニーズにお応えします。マグカップ、Tシャツ、ステンレスボトルなど幅広い印刷サービスを提供。代表挨拶、事業内容、サービスの流れをご紹介します。',
    keywords: '印刷サービス,紙印刷,シルク印刷,レーザー印刷,マグカップ印刷,Tシャツ印刷,岡山市,おかやま印刷',
    canonical: '/services',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: '印刷サービス',
      provider: {
        '@type': 'LocalBusiness',
        name: 'おかやま印刷株式会社',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '北区○○町1-2-3',
          addressLocality: '岡山市',
          addressRegion: '岡山県',
          addressCountry: 'JP',
        },
        telephone: '+81-86-012-3456',
      },
      areaServed: {
        '@type': 'City',
        name: '岡山市',
      },
      description: '紙印刷、シルク印刷、レーザー印刷など多彩な印刷技術でお客様のニーズにお応えします。',
    },
  });

  const services = [
    {
      number: '01',
      icon: 'ri-cup-line',
      title: 'マグカップ印刷',
      description: 'オリジナルデザインのマグカップを1個から制作可能です。卒団記念品、ノベルティ、販促品として人気があります。フルカラー印刷で写真やイラストも美しく再現できます。',
      features: ['記念品マグカップ', 'ノベルティマグカップ', 'オリジナルデザイン', '小ロット対応'],
      image: 'https://public.readdy.ai/ai/img_res/cbd2f288b4a88c6a3e0cd853adb587d6.jpg',
    },
    {
      number: '02',
      icon: 'ri-flask-line',
      title: 'レーザー印刷',
      description: 'ステンレスボトル、金属製品、木製品などへの高精度なレーザー印刷を行っています。彫刻のような高級感のある仕上がりで、長期間使用しても消えることがありません。',
      features: ['ステンレスボトル', '金属製品', '木製品', 'アクリル製品'],
      image: 'https://readdy.ai/api/search-image?query=Premium%20stainless%20steel%20bottles%20and%20metal%20products%20with%20precise%20laser%20engraved%20designs%20displayed%20elegantly%20on%20a%20clean%20white%20surface%20with%20professional%20studio%20lighting%20and%20a%20minimalist%20plain%20background%20highlighting%20the%20sophisticated%20finish%20and%20permanent%20engraving%20quality&width=800&height=600&seq=service104&orientation=landscape',
    },
    {
      number: '03',
      icon: 'ri-t-shirt-line',
      title: 'シルク印刷',
      description: 'Tシャツ、トートバッグ、タオルなど、布製品への印刷を得意としています。鮮やかな発色と優れた耐久性が特徴で、洗濯を繰り返しても色落ちしにくい高品質な仕上がりをお約束します。',
      features: ['Tシャツ・ポロシャツ', 'トートバッグ・エコバッグ', 'タオル・手ぬぐい', 'キャップ・帽子'],
      image: 'https://readdy.ai/api/search-image?query=Colorful%20silk%20screen%20printed%20t-shirts%20tote%20bags%20and%20towels%20with%20vibrant%20designs%20displayed%20on%20a%20clean%20white%20surface%20with%20professional%20studio%20lighting%20and%20a%20minimalist%20plain%20background%20showing%20excellent%20color%20saturation%20print%20quality%20and%20durability&width=800&height=600&seq=service102&orientation=landscape',
    },
    {
      number: '04',
      icon: 'ri-printer-line',
      title: '紙印刷',
      description: 'チラシ、パンフレット、名刺、ポスターなど、あらゆる紙媒体の印刷に対応しています。高品質なオフセット印刷からオンデマンド印刷まで、用途に応じて最適な印刷方法をご提案いたします。',
      features: ['チラシ・フライヤー', 'パンフレット・カタログ', '名刺・ショップカード', 'ポスター・パネル'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20high%20quality%20paper%20printing%20materials%20including%20colorful%20brochures%20flyers%20business%20cards%20and%20posters%20arranged%20neatly%20on%20a%20clean%20white%20surface%20with%20soft%20studio%20lighting%20and%20a%20minimalist%20plain%20background%20showcasing%20excellent%20print%20quality%20vibrant%20colors%20and%20sharp%20details&width=800&height=600&seq=service101&orientation=landscape',
    },
    {
      number: '05',
      icon: 'ri-gift-line',
      title: 'ノベルティ制作',
      description: 'ボールペン、スマホケース、シール、ステッカーなど、多彩なノベルティグッズの制作を承っています。企業の販促品やイベント記念品として、オリジナルデザインで印刷いたします。',
      features: ['ボールペン・文房具', 'スマホケース・アクセサリー', 'シール・ステッカー', '販促グッズ全般'],
      image: 'https://readdy.ai/api/search-image?query=A%20variety%20of%20promotional%20novelty%20items%20including%20custom%20printed%20ballpoint%20pens%20smartphone%20cases%20stickers%20and%20decals%20arranged%20attractively%20on%20a%20clean%20white%20surface%20with%20professional%20studio%20lighting%20and%20a%20minimalist%20plain%20background%20showcasing%20corporate%20branding%20and%20high%20quality%20printing&width=800&height=600&seq=service105&orientation=landscape',
    },
    {
      number: '06',
      icon: 'ri-sparkling-line',
      title: 'サンドブラスト彫刻',
      description: 'ガラス、ミラー（鏡）、タイルなどへのサンドブラスト彫刻を行っています。繊細で美しい彫刻表現が可能で、記念品や贈答品、インテリアアイテムとして高い人気を誇ります。',
      features: ['ガラス製品彫刻', 'ミラー（鏡）彫刻', 'タイル彫刻', '記念品・贈答品制作'],
      image: 'https://readdy.ai/api/search-image?query=Elegant%20sandblasted%20glass%20products%20mirrors%20and%20tiles%20with%20intricate%20etched%20designs%20displayed%20beautifully%20on%20a%20clean%20white%20surface%20with%20soft%20studio%20lighting%20and%20a%20minimalist%20plain%20background%20showcasing%20delicate%20craftsmanship%20and%20premium%20quality%20memorial%20gifts&width=800&height=600&seq=service106&orientation=landscape',
    },
  ];

  const serviceFlow = [
    {
      step: '01',
      title: 'お問い合わせ',
      description: 'お電話またはお問い合わせフォームからご連絡ください。ご要望をお聞かせいただきます。',
      icon: 'ri-phone-line',
    },
    {
      step: '02',
      title: 'お見積もり',
      description: '印刷内容、数量、納期などを確認し、無料でお見積もりをご提示いたします。',
      icon: 'ri-file-text-line',
    },
    {
      step: '03',
      title: 'デザイン確認',
      description: 'デザインデータをご確認いただき、修正があれば対応いたします。',
      icon: 'ri-palette-line',
    },
    {
      step: '04',
      title: '印刷開始',
      description: 'デザイン確定後、印刷作業を開始いたします。',
      icon: 'ri-printer-line',
    },
    {
      step: '05',
      title: '品質チェック',
      description: '印刷後、品質を厳しくチェックし、問題がないことを確認します。',
      icon: 'ri-checkbox-circle-line',
    },
    {
      step: '06',
      title: '納品',
      description: 'ご指定の場所へお届け、またはご来社いただきお渡しいたします。',
      icon: 'ri-truck-line',
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary via-[#FF7A30] to-[#E85A20]"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg">
              サービス案内
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl text-white drop-shadow-md">
              お客様の想いを、確かな技術でカタチにします
            </p>
          </div>
        </section>

        {/* Greeting Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="px-4 md:px-8 lg:px-16">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                <div className="order-2 lg:order-1">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20friendly%20Japanese%20businessman%20in%20his%2050s%20wearing%20work%20clothes%20with%20a%20genuine%20smile%20standing%20in%20a%20professional%20printing%20workshop%20with%20printing%20equipment%20in%20the%20background%20showing%20dedication%20and%20trustworthiness%20with%20natural%20lighting%20and%20a%20clean%20organized%20environment&width=800&height=1000&seq=president001&orientation=portrait"
                    alt="代表取締役 岡山太郎"
                    className="w-full h-auto rounded-3xl shadow-2xl"
                  />
                </div>
                <div className="order-1 lg:order-2">
                  <span className="inline-block px-4 py-1 bg-primary/10 text-primary text-sm font-bold rounded-full mb-4">
                    GREETING
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    代表取締役からのご挨拶
                  </h2>
                  <div className="space-y-4 text-gray-700 leading-relaxed">
                    <p>
                      おかやま印刷株式会社のホームページをご覧いただき、誠にありがとうございます。代表取締役の岡山太郎と申します。
                    </p>
                    <p>
                      当社は1992年の創業以来、岡山市を中心に地域の皆様に支えられながら、印刷業を営んでまいりました。紙印刷からシルク印刷、レーザー印刷まで、多彩な印刷技術を駆使し、お客様のあらゆるご要望にお応えしています。
                    </p>
                    <p>
                      私たちが最も大切にしているのは、お客様ひとりひとりの想いや気持ちを形にすることです。卒団記念品、販促物、ノベルティなど、それぞれに込められた想いを理解し、親身に対応させていただくことをモットーとしています。
                    </p>
                    <p>
                      小ロットから大量生産まで、どのようなご相談でもお気軽にお問い合わせください。お客様の期待を超える仕上がりをお約束いたします。
                    </p>
                    <p className="font-bold text-gray-900 pt-4">
                      代表取締役　岡山太郎
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Detail Section */}
        <section className="py-16 md:py-24 bg-primary-light">
          <div className="px-4 md:px-8 lg:px-16">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                事業内容
              </h2>
              <p className="text-lg text-gray-600">
                多彩な印刷技術でお客様のニーズにお応えします
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {services.map((service, index) => (
                <article
                  key={index}
                  className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{service.number}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                        <i className={`${service.icon} text-2xl text-primary`}></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      {service.description}
                    </p>
                    <div className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <i className="ri-check-line text-primary"></i>
                          <span className="text-sm text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Service Flow Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="px-4 md:px-8 lg:px-16">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12 md:mb-16">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  サービスの流れ
                </h2>
                <p className="text-lg text-gray-600">
                  お問い合わせから納品までの流れをご説明します
                </p>
              </div>

              <div className="space-y-6">
                {serviceFlow.map((flow, index) => (
                  <div key={index} className="relative">
                    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 flex items-center gap-6">
                      <div className="flex-shrink-0 w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-xl">{flow.step}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                          {flow.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {flow.description}
                        </p>
                      </div>
                      <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                        <i className={`${flow.icon} text-2xl text-primary`}></i>
                      </div>
                    </div>
                    {index < serviceFlow.length - 1 && (
                      <div className="absolute left-8 top-full w-0.5 h-6 bg-primary/30 -translate-x-1/2"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/72f95f79575f04d2e5e777734d1956f7.jpeg"
              alt="印刷製品"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>

          <div className="relative z-10 px-4 md:px-8 lg:px-16">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                お気軽にご相談ください
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-12">
                お見積もりは無料です。お電話またはお問い合わせフォームからお気軽にどうぞ。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6">
                <a
                  href="tel:086-012-3456"
                  className="w-full sm:w-auto px-8 py-4 bg-primary text-white rounded-full font-bold text-base md:text-lg hover:bg-primary-dark transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-xl"></i>
                  086-012-3456
                </a>
                <a
                  href="/contact"
                  className="w-full sm:w-auto px-8 py-4 bg-white text-primary border-2 border-primary rounded-full font-bold text-base md:text-lg hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line text-xl"></i>
                  お問い合わせフォーム
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServicesPage;
